/**
 * Datos de diagnóstico diferencial bayesiano por caso
 * Cada hallazgo tiene un delta de probabilidad para cada diagnóstico posible
 */

export interface DifferentialConfig {
  caseId: string;
  differentials: DifferentialDx[];
}

export interface DifferentialDx {
  name: string;
  code: string;
  initialProbability: number; // 0-100
  color: string;
  findings: Record<string, number>; // nodeId → delta probabilidad (+/-)
  keyFindings: string[]; // nodeIds que son críticos para este dx
  clinicalPearl: string; // enseñanza clave
}

export const DIFFERENTIAL_CASES: Record<string, DifferentialConfig> = {
  iam: {
    caseId: 'iam',
    differentials: [
      {
        name: 'Infarto Agudo de Miocardio',
        code: '22298006',
        initialProbability: 25,
        color: '#EF4444',
        findings: {
          'sintoma-1': 20,  // Dolor torácico opresivo → +20% para IAM
          'vital-1': 8,     // FC 110 → +8%
          'vital-2': 5,     // PA 90 → +5%
          'lab-1': 30,      // Troponina 850 → +30% (hallazgo clave)
          'dx-1': 0,        // El diagnóstico no suma (está oculto)
        },
        keyFindings: ['lab-1', 'sintoma-1'],
        clinicalPearl: 'La troponina T > 14 ng/L es el biomarcador más específico. Un valor de 850 ng/L con dolor torácico opresivo es virtualmente diagnóstico de IAM.',
      },
      {
        name: 'Tromboembolia Pulmonar',
        code: '59282003',
        initialProbability: 20,
        color: '#8B5CF6',
        findings: {
          'sintoma-1': 5,   // Dolor torácico → algo compatible pero no típico
          'vital-1': 10,    // Taquicardia → compatible
          'vital-2': 3,     // Hipotensión → compatible
          'lab-1': -25,     // Troponina MUY elevada → descarta TEP (en TEP es leve)
        },
        keyFindings: ['lab-1'],
        clinicalPearl: 'En TEP la troponina puede elevarse levemente por sobrecarga del VD, pero valores > 100 ng/L son más sugestivos de IAM.',
      },
      {
        name: 'Disección Aórtica',
        code: '308546005',
        initialProbability: 15,
        color: '#F59E0B',
        findings: {
          'sintoma-1': 8,   // Dolor torácico → compatible (pero transfixiante, no opresivo)
          'vital-1': 5,     // Taquicardia → compatible
          'vital-2': 10,    // Hipotensión → muy compatible
          'lab-1': -15,     // Troponina elevada → menos probable (salvo disección tipo A con compromiso coronario)
        },
        keyFindings: ['sintoma-1'],
        clinicalPearl: 'La disección aórtica causa dolor "desgarrante" de inicio súbito, a diferencia del dolor "opresivo" progresivo del IAM. La asimetría de pulsos y PA es un hallazgo clave.',
      },
      {
        name: 'Pericarditis Aguda',
        code: '3238004',
        initialProbability: 15,
        color: '#10B981',
        findings: {
          'sintoma-1': -5,  // Dolor opresivo → en pericarditis es pleurítico, no opresivo
          'vital-1': 3,     // Taquicardia → posible
          'vital-2': -8,    // Hipotensión → raro en pericarditis simple
          'lab-1': -20,     // Troponina 850 → demasiado alta para pericarditis
        },
        keyFindings: ['sintoma-1', 'lab-1'],
        clinicalPearl: 'La pericarditis causa dolor pleurítico que mejora sentado e inclinado hacia adelante. La troponina puede elevarse levemente pero valores > 100 sugieren miopericarditis o IAM.',
      },
      {
        name: 'Neumotórax a Tensión',
        code: '233604007',
        initialProbability: 10,
        color: '#06B6D4',
        findings: {
          'sintoma-1': 0,   // Dolor → puede ser compatible
          'vital-1': 5,     // Taquicardia → compatible
          'vital-2': 8,     // Hipotensión → muy compatible
          'lab-1': -20,     // Troponina elevada → no explicada por neumotórax
        },
        keyFindings: ['vital-2'],
        clinicalPearl: 'El neumotórax a tensión se presenta con disnea súbita, desviación traqueal y ausencia de murmullo vesicular unilateral. La troponina debería ser normal.',
      },
    ],
  },

  sepsis: {
    caseId: 'sepsis',
    differentials: [
      {
        name: 'Sepsis / Shock Séptico',
        code: '91302008',
        initialProbability: 25,
        color: '#EF4444',
        findings: {
          'sintoma-1': 15,  // Fiebre → muy sugestivo
          'sintoma-2': 20,  // Confusión → disfunción orgánica (qSOFA)
          'vital-1': 15,    // PA 85 → shock
          'lab-1': 25,      // Lactato 4.2 → confirma hipoperfusión
          'lab-2': 8,       // Leucocitosis → respuesta inflamatoria
        },
        keyFindings: ['lab-1', 'sintoma-2', 'vital-1'],
        clinicalPearl: 'Sepsis-3 = infección sospechada + SOFA ≥ 2. Shock séptico = sepsis + lactato > 2 mmol/L + vasopresores para mantener PAM ≥ 65. Mortalidad > 40%.',
      },
      {
        name: 'Meningitis Bacteriana',
        code: '95883001',
        initialProbability: 20,
        color: '#F59E0B',
        findings: {
          'sintoma-1': 10,  // Fiebre → compatible
          'sintoma-2': 15,  // Confusión → muy compatible
          'vital-1': 5,     // Hipotensión → puede ocurrir
          'lab-1': 5,       // Lactato → elevado por sepsis asociada
          'lab-2': 8,       // Leucocitosis → compatible
        },
        keyFindings: ['sintoma-2'],
        clinicalPearl: 'La meningitis tiene la triada clásica: fiebre + cefalea + rigidez de nuca. La confusión es tardía. Falta cefalea y rigidez nucal en este caso.',
      },
      {
        name: 'Golpe de Calor',
        code: '52072009',
        initialProbability: 10,
        color: '#10B981',
        findings: {
          'sintoma-1': 5,   // Fiebre → compatible pero > 40°C
          'sintoma-2': 10,  // Confusión → compatible
          'vital-1': -5,    // Hipotensión → menos típico inicialmente
          'lab-1': -10,     // Lactato → no tan elevado típicamente
          'lab-2': -5,      // Leucocitosis → no es hallazgo principal
        },
        keyFindings: ['sintoma-1'],
        clinicalPearl: 'Golpe de calor: T > 40°C + alteración neurológica + anhidrosis en contexto de exposición al calor. Falta el contexto ambiental.',
      },
      {
        name: 'Endocarditis Infecciosa',
        code: '56819008',
        initialProbability: 12,
        color: '#8B5CF6',
        findings: {
          'sintoma-1': 10,  // Fiebre → cardinal
          'sintoma-2': 5,   // Confusión → por embolismo séptico
          'vital-1': 3,     // Hipotensión → posible
          'lab-1': 3,       // Lactato → leve elevación
          'lab-2': 8,       // Leucocitosis → compatible
        },
        keyFindings: ['sintoma-1'],
        clinicalPearl: 'Endocarditis: fiebre + soplo nuevo + fenómenos embólicos (Janeway, Osler). Evolución más insidiosa que sepsis aguda.',
      },
    ],
  },

  epoc: {
    caseId: 'epoc',
    differentials: [
      {
        name: 'Exacerbación de EPOC',
        code: '195951007',
        initialProbability: 30,
        color: '#10B981',
        findings: {
          'hist-1': 25,     // Antecedente EPOC → muy fuerte
          'sintoma-1': 15,  // Disnea → cardinal
          'vital-1': 10,    // FR 28 → taquipnea
          'vital-2': 15,    // SpO2 86% → hipoxemia
        },
        keyFindings: ['hist-1', 'vital-2'],
        clinicalPearl: 'EPOC exacerbado: aumento de disnea + tos + esputo sobre la línea basal. SpO2 target 88-92% (no 100%) para evitar hipercapnia por pérdida del drive hipóxico.',
      },
      {
        name: 'Insuficiencia Cardíaca Descompensada',
        code: '84114007',
        initialProbability: 20,
        color: '#EF4444',
        findings: {
          'hist-1': -5,     // EPOC → no explica IC directamente
          'sintoma-1': 10,  // Disnea → cardinal también en IC
          'vital-1': 5,     // Taquipnea → compatible
          'vital-2': 8,     // Hipoxemia → compatible (edema pulmonar)
        },
        keyFindings: ['sintoma-1'],
        clinicalPearl: 'IC descompensada: ortopnea, DPN, edema periférico, crepitantes bibasales, BNP elevado. Sin estos hallazgos, EPOC es más probable.',
      },
      {
        name: 'Neumonía',
        code: '233604007',
        initialProbability: 20,
        color: '#F59E0B',
        findings: {
          'hist-1': 5,      // EPOC → factor de riesgo para neumonía
          'sintoma-1': 10,  // Disnea → compatible
          'vital-1': 8,     // Taquipnea → compatible
          'vital-2': 10,    // Hipoxemia → compatible
        },
        keyFindings: ['vital-1', 'vital-2'],
        clinicalPearl: 'Neumonía sobre EPOC es común. Buscar: fiebre, tos productiva, consolidación en Rx tórax. Sin fiebre ni infiltrados, EPOC puro es más probable.',
      },
      {
        name: 'TEP',
        code: '59282003',
        initialProbability: 15,
        color: '#8B5CF6',
        findings: {
          'hist-1': 0,      // EPOC → no descarta TEP
          'sintoma-1': 8,   // Disnea súbita → muy sugestivo
          'vital-1': 5,     // Taquipnea → compatible
          'vital-2': 10,    // Hipoxemia → compatible
        },
        keyFindings: ['sintoma-1'],
        clinicalPearl: 'TEP: disnea de inicio SÚBITO (vs progresivo en EPOC). Score de Wells + dímero D para estratificar. EPOC no excluye TEP — pueden coexistir.',
      },
    ],
  },
};
